import React, { useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const localDateTime = (date: Date) => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

type Statistics = Awaited<ReturnType<typeof adminAPI.getExternalPlatformStatistics>>;

export const ExternalPlatformStatistics: React.FC<{ platformId: string }> = ({ platformId }) => {
    const [dateFrom, setDateFrom] = useState(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return localDateTime(date);
    });
    const [dateTo, setDateTo] = useState(() => localDateTime(new Date()));
    const [statistics, setStatistics] = useState<Statistics | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = async (event: React.FormEvent) => {
        event.preventDefault();
        setStatistics(null);
        setError('');
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from >= to) {
            setError('Укажите период: начало должно быть раньше окончания.');
            return;
        }
        setLoading(true);
        try {
            setStatistics(await adminAPI.getExternalPlatformStatistics(platformId, from.toISOString(), to.toISOString()));
        } catch (err) {
            setError('Не удалось загрузить статистику. Попробуйте ещё раз.');
        } finally {
            setLoading(false);
        }
    };

    return <section className="space-y-4 rounded-lg border bg-slate-50 dark:bg-slate-900 p-4" aria-label="Статистика платформы">
        <div>
            <h3 className="font-semibold">Оборот платформы в USDT</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Успешные транзакции по времени завершения. Начало включительно, окончание не включается.</p>
        </div>
        <form onSubmit={load} className="flex flex-wrap items-end gap-3">
            <label className="space-y-1 text-sm">С
                <Input type="datetime-local" required value={dateFrom} disabled={loading} onChange={e => { setDateFrom(e.target.value); setStatistics(null); setError(''); }} />
            </label>
            <label className="space-y-1 text-sm">До
                <Input type="datetime-local" required value={dateTo} disabled={loading} onChange={e => { setDateTo(e.target.value); setStatistics(null); setError(''); }} />
            </label>
            <Button type="submit" disabled={loading}>{loading ? 'Загрузка…' : 'Показать'}</Button>
        </form>
        {error && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{error}</p>}
        {statistics && <div aria-live="polite" className="space-y-6">
            {([
                { title: 'Пополнения', summary: statistics.deposit },
                { title: 'Выводы', summary: statistics.withdraw },
            ]).map(({ title, summary }) => <section key={title} aria-label={title} className="space-y-2">
                <h4 className="font-semibold">{title}</h4>
                <div className="grid gap-3 md:grid-cols-3">
                    {[
                        { label: 'По данным провайдера', amount: summary.total_usdt, count: summary.reported_count },
                        { label: 'По нашему курсу, до комиссии', amount: summary.gross_usdt, count: summary.gross_count },
                        { label: 'По нашему курсу, после комиссии', amount: summary.net_usdt, count: summary.net_count },
                    ].map(item => <div key={item.label} className="rounded-md border bg-white dark:bg-slate-800 p-3">
                        <p className="text-sm text-slate-500 dark:text-slate-400">{item.label}</p>
                        <p className="text-xl font-semibold">{item.amount.toLocaleString('ru-RU', { maximumFractionDigits: 8 })} USDT</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Учтено: {item.count} из {summary.completed_count}</p>
                        {item.count < summary.completed_count && <p className="text-xs text-amber-700 dark:text-amber-300">Неполный итог: для {summary.completed_count - item.count} транзакций нет суммы.</p>}
                    </div>)}
                </div>
                <p className="text-sm">Успешных транзакций: {summary.completed_count}. С суммой USDT: {summary.reported_count}.</p>
                {summary.completed_count === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">За выбранный период завершённых транзакций нет.</p>}
                {summary.missing_usdt_count > 0 && <p className="text-sm text-amber-700 dark:text-amber-300">Без суммы USDT от провайдера: {summary.missing_usdt_count}. Они не включены в оборот — итог неполный.</p>}
            </section>)}
        </div>}
        <p className="text-xs text-slate-500 dark:text-slate-400">Данные провайдера и наш расчёт показаны отдельно. Расчёт использует курс и комиссию, сохранённые в транзакции; текущие настройки не меняют историю. Пополнения и выводы считаются отдельно. Это оборот, а не прибыль.</p>
    </section>;
};
